/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, put, select, take, takeEvery,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';

import mbpUtil from 'mbp-api-util';
import {
    addGreetingCardGiftMessage,
    addGreetingCardGiftMessageCompleted,
} from './GiftMessage-Actions';

import { checkJWT } from '../../../../Member/ducks/Auth/Auth-Operations';
import { getOrderId } from '../../../../Common/Common-Selectors';
import { getFeatureFlags, getRelationshipListById } from '../../App/App-Selectors';
import { addGreetingCardIsle } from '../../../../Recipient/ducks/CardIsle/CardIsle-Operations';
import { addCardIsleComplete } from '../../../../Recipient/ducks/CardIsle/CardIsle-Actions';
import { workerAddCGCData } from '../../../../CGC/CGC-Operations';
import { addCGCDataToApiCompleted } from '../../../../CGC/CGC-Actions';
import { retrieveCart } from '../../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { flagOffShowLoadingMessage } from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { clearErrors, clearOrderItemErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import cartServiceClient from '../../../../../../apis/cart-service-apis/cartServiceClient';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { updateChatgptGiftMessage } from '../ChatGptMessage/ChatGptMessage-Actions';
import { getChatGptMessageData } from '../ChatGptMessage/ChatGptMessage-Selectors';
import { prepareRetreiveCart } from '../../Cart/ducks/RetrieveCart/PrepareRetreiveCart-Operations';
import { trackEvent } from '../../../../TagManager/ducks/TagManager/TagManager-Actions';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

function* workerGreetingMessage(action) {
    try {
        const jwtToken = yield call(checkJWT);
        const orderId = yield select(getOrderId);

        const { greetingData, pageType } = action.data;
        const isRecipientData = !!(action.data.recipient?.firstName) || false;
        const typeGreeting = greetingData.type || 'giftMessage';

        const featureFlags = yield select(getFeatureFlags);
        const cardIsleEnabled = featureFlags['is-card-isle-enabled'] || false;
        const cgcBrandsConfig = featureFlags['which-brands-allow-cgc-greeting']?.['show-cgc-greeting-card'];
        const chatGPTEnabled = featureFlags['is-chat-gpt-enabled'] || false;
        const cgcCardEnabled = !!(cgcBrandsConfig?.includes(action.data.brandCode));

        yield put(clearOrderItemErrors(
            action.data.orderItemId,
            'giftMessage',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            orderId,
            'payment',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearErrors({
            errType: 'giftMessage',
            field: 'error',
        }));

        if (cardIsleEnabled && typeGreeting === 'cardIsle') {
            const cardIsleData = action.data;
            cardIsleData.isRecipientData = isRecipientData;
            yield fork(addGreetingCardIsle, cardIsleData);
            yield take(addCardIsleComplete().type);
        } else if (cgcCardEnabled && typeGreeting === 'cgcGreetings') {
            const cgcData = {
                ...greetingData.data,
                callBackHandler: action.data.handleCallBack,
                pageType: action?.data?.pageType,
                isRecipientData,
            };

            yield fork(workerAddCGCData, cgcData);
            yield take(addCGCDataToApiCompleted().type);
        } else {
            // Save gift message
            const giftMessageObj = greetingData.data || {};

            let giftMsgText = '';
            if (!greetingData.type) {
                giftMsgText = 'No Card Message';
            } else {
                giftMsgText = (giftMessageObj.message || '');
            }

            let giftAuthor = (giftMessageObj?.signature || '');
            const occasionCode = giftMessageObj?.chatGPToccCode || giftMessageObj?.occasionCode || '';

            if (giftMsgText.indexOf('From: ') >= 0) {
                const fullMessage = giftMsgText.split('From: ');
                if (fullMessage.length > 0 && fullMessage[0] !== 'No Card Message') {
                    giftMsgText = fullMessage[0];
                    if (fullMessage[1]) {
                        giftAuthor = fullMessage[1];
                    }
                }
            }

            if (giftMessageObj.orderGreetingId) {
                const removeConfigObj = {
                    env: {},
                    jwtToken,
                    orderItemId: giftMessageObj.orderGreetingId,
                    cartId: orderId,
                };
                yield call(cartServiceClient.removeOrderItem, removeConfigObj);
            }

            const payload = {
                env: {},
                jwtToken,
                cartItemId: action.data.orderItemId,
                greeting: {
                    message: giftMsgText || 'No Card Message',
                    signature: giftAuthor || '',
                    occasionCode,
                },
                useMessageForAll: false,
                cartId: orderId,
            };

            if (giftMessageObj.useThisMessageForAll) {
                payload.useMessageForAll = true;
            }
            const responseGiftMessage = yield call(cartServiceClient.saveGreetingMessage, payload);
            if (responseGiftMessage?.data?.result === 'SUCCESS' && action.data?.handleCallBack) {
                if (greetingData?.data?.message) {
                    yield put(trackEvent({
                        eventCategory: 'Checkout',
                        eventAction: 'Gift Message',
                        eventLabel: greetingData?.data?.message === 'No Card Message' ? 'No Gift Message Selected' : 'Complimentary Gift Message Selected',
                    }));
                }

                if (chatGPTEnabled && action.data.orderItemId) {
                    const currentChatGPTData = yield select(getChatGptMessageData);
                    if (currentChatGPTData?.[action.data.orderItemId]?.giftMessage) {
                        if (
                            action.data
                            && action.data.recipientAddress
                            && action.data.orderItem
                            && currentChatGPTData[action.data.orderItemId].relation
                            && action.data.orderItem.relationship !== currentChatGPTData[action.data.orderItemId].relation
                        ) {
                            const relationshipList = yield select(getRelationshipListById);
                            const relation = currentChatGPTData?.[action.data.orderItemId]?.relation || null;

                            const reqObj = {
                                env: {},
                                jwtToken,
                                cartId: orderId,
                                cartItemId: action.data.orderItemId,
                                useAddressForAll: false,
                                recipient: {
                                    ...action.data.recipientAddress,
                                },
                                occasions: action.data.orderItem?.occasions || null,
                                relationship: relation ? relationshipList[relation] : null,
                            };

                            // // Save Recipient Address
                            yield call(cartServiceClient.saveRecipient, reqObj);
                        }

                        yield put(updateChatgptGiftMessage(action.data.orderItemId, {}));
                    }
                }

                if (!isRecipientData) {
                    yield call(retrieveCart);
                }

                yield call(prepareRetreiveCart, pageType);

                yield call(action.data.handleCallBack, false);
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'GiftMessage-Operation.js',
            function: 'saveRecipientGiftMessage',
            message: 'Enterprise: Error saving giftmessage page',
            jsError: ex,
            greetingData: action.data.greetingData,
        });

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'giftMessage',
                errorValues: ex.response?.data,
            },
        });

        yield put(flagOffShowLoadingMessage());
    }

    if (REACT_APP_INJECTABLE === 'false') {
        yield put(flagOffShowLoadingMessage());
    }

    yield put(addGreetingCardGiftMessageCompleted());
}

function* watchUpdateGreetingCardGiftMessage() {
    yield takeEvery(addGreetingCardGiftMessage().type, workerGreetingMessage);
}

const watchers = [
    fork(watchUpdateGreetingCardGiftMessage),
];

export { watchers, workerGreetingMessage };

export default {};
